/**
 * MetadataDatabase — /metadata
 *
 * Central hub for all metadata sheets across all projects.
 *
 * Layout:
 *   - Header with page title and total sheet count.
 *   - One card per project, showing all its sheets in a mini table.
 *   - Per-sheet actions: View (navigates to the viewer), Edit (opens in-app editor),
 *     Download (exports as CSV).
 *   - "View All / New Sheet" button on each project card goes to the full viewer.
 *   - "New Analysis" shortcut in the header for starting a new project.
 *
 * Empty states:
 *   - No projects at all → prompt to start first analysis.
 *   - Project exists but has no sheets → "No sheets yet" row with a New Sheet link.
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
    FileSpreadsheet,
    Plus,
    Pencil,
    Download,
    ExternalLink,
    Loader2,
    XCircle,
    ChevronRight,
    Database,
    FolderOpen,
    Trash2,
    ChevronDown,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { metadataAPI } from '../services/api';
import { ConfirmModal } from '../components/ui/confirm-modal';

/** Human-readable label + colour for each sheet type pill. */
const TYPE_STYLES = {
    batch:  { label: 'Batch',  className: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' },
    master: { label: 'Master', className: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300' },
    project:{ label: 'Project',className: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300' },
};

function TypeBadge({ type }) {
    const style = TYPE_STYLES[type] || TYPE_STYLES.project;
    return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${style.className}`}>
            {style.label}
        </span>
    );
}

export default function MetadataDatabase() {
    const navigate = useNavigate();
    const [groups, setGroups] = useState([]);  // [{ project_id, project_name, sheets: [...] }]
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    // { sheetId, sheetName, projectId } while the delete modal is open; null otherwise.
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);
    // New-sheet project selector modal
    const [newSheetOpen, setNewSheetOpen] = useState(false);
    const [newSheetProjectId, setNewSheetProjectId] = useState('');
    // New analysis pipeline selector
    const [showAnalysisMenu, setShowAnalysisMenu] = useState(false);

    useEffect(() => {
        const fetch = async () => {
            try {
                const res = await metadataAPI.listAll();
                setGroups(res.data);
            } catch (err) {
                setError(err.response?.data?.detail || 'Failed to load metadata');
            } finally {
                setLoading(false);
            }
        };
        fetch();
    }, []);

    const handleDownload = async (sheetId, sheetName) => {
        try {
            const res = await metadataAPI.exportCSV(sheetId);
            const blob = new Blob([res.data], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${sheetName || 'metadata'}.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch {
            setError('Failed to download CSV');
            setTimeout(() => setError(''), 4000);
        }
    };

    const handleDeleteSheet = async () => {
        if (!deleteTarget) return;
        try {
            setDeleting(true);
            await metadataAPI.deleteSheet(deleteTarget.sheetId);
            // Remove the deleted sheet from the local groups state without re-fetching.
            setGroups(prev => prev.map(g =>
                g.project_id === deleteTarget.projectId
                    ? { ...g, sheets: g.sheets.filter(s => s.id !== deleteTarget.sheetId) }
                    : g
            ));
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to delete sheet');
            setTimeout(() => setError(''), 5000);
        } finally {
            setDeleting(false);
            setDeleteTarget(null);
        }
    };

    const totalSheets = groups.reduce((sum, g) => sum + g.sheets.length, 0);

    return (
        <>
        <div className="min-h-screen bg-background">
            {/* ── Page header ────────────────────────────────────────────── */}
            <header className="border-b bg-card">
                <div className="container mx-auto px-4 py-6">
                    <div className="flex items-start justify-between">
                        <div>
                            <h1 className="text-3xl font-bold flex items-center gap-2">
                                <Database className="h-7 w-7" />
                                Metadata Database
                            </h1>
                            <p className="text-muted-foreground mt-1">
                                All metadata sheets, organised by project
                            </p>
                            {!loading && (
                                <div className="flex gap-3 mt-3">
                                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-muted text-muted-foreground">
                                        {groups.length} {groups.length === 1 ? 'project' : 'projects'}
                                    </span>
                                    {totalSheets > 0 && (
                                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                                            <FileSpreadsheet className="h-3.5 w-3.5" />
                                            {totalSheets} {totalSheets === 1 ? 'sheet' : 'sheets'}
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className="flex gap-2">
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setNewSheetProjectId(groups[0]?.project_id?.toString() || '');
                                    setNewSheetOpen(true);
                                }}
                                disabled={groups.length === 0}
                            >
                                <Plus className="h-4 w-4 mr-1.5" />
                                New Sheet
                            </Button>
                            <div className="relative">
                                <Button
                                    onClick={() => setShowAnalysisMenu(!showAnalysisMenu)}
                                    className="flex items-center gap-1.5"
                                >
                                    <FolderOpen className="h-4 w-4" />
                                    New Analysis
                                    <ChevronDown className="h-4 w-4" />
                                </Button>

                                {showAnalysisMenu && (
                                    <>
                                        <div
                                            className="fixed inset-0 z-10"
                                            onClick={() => setShowAnalysisMenu(false)}
                                        />
                                        <div className="absolute right-0 mt-2 w-80 bg-card border border-border rounded-lg shadow-lg z-20 overflow-hidden">
                                            <button
                                                className="w-full text-left px-4 py-3 hover:bg-accent transition-colors border-b border-border"
                                                onClick={() => {
                                                    setShowAnalysisMenu(false);
                                                    navigate('/project/batch/new');
                                                }}
                                            >
                                                <div className="font-medium text-sm">16S rRNA (EPI2ME)</div>
                                                <div className="text-xs text-muted-foreground">Bacterial community profiling</div>
                                            </button>
                                            <button
                                                className="w-full text-left px-4 py-3 hover:bg-accent transition-colors"
                                                onClick={() => {
                                                    setShowAnalysisMenu(false);
                                                    navigate('/project/coi/new');
                                                }}
                                            >
                                                <div className="font-medium text-sm">COI / Eukaryota (QIIME 2)</div>
                                                <div className="text-xs text-muted-foreground">Eukaryote eDNA profiling</div>
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            <main className="container mx-auto px-4 py-8 space-y-6">
                {/* Error */}
                {error && (
                    <div className="flex items-center gap-2 text-sm text-destructive bg-destructive/10 border border-destructive/20 px-4 py-3 rounded-lg">
                        <XCircle className="h-4 w-4 shrink-0" />
                        {error}
                    </div>
                )}

                {/* Loading */}
                {loading && (
                    <div className="flex items-center justify-center py-16">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                        <span className="ml-3 text-muted-foreground">Loading metadata...</span>
                    </div>
                )}

                {/* No projects at all */}
                {!loading && groups.length === 0 && (
                    <Card>
                        <CardContent className="py-16 text-center">
                            <Database className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                            <h3 className="text-lg font-medium mb-2">No projects yet</h3>
                            <p className="text-muted-foreground mb-6">
                                Start your first analysis to create a project and add metadata.
                            </p>
                            <div className="flex flex-col gap-3 items-center">
                                <Button onClick={() => navigate('/project/batch/new')}>
                                    16S rRNA (EPI2ME)
                                </Button>
                                <Button variant="outline" onClick={() => navigate('/project/coi/new')}>
                                    COI / Eukaryota (QIIME 2)
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Project groups */}
                {!loading && groups.map((group, groupIdx) => (
                    <motion.div
                        key={group.project_id}
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: groupIdx * 0.04 }}
                    >
                        <Card>
                            {/* Project header */}
                            <CardHeader className="pb-0">
                                <div className="flex items-center justify-between">
                                    <CardTitle className="flex items-center gap-2 text-base">
                                        <FolderOpen className="h-4 w-4 text-muted-foreground" />
                                        {group.project_name || `Project #${group.project_id}`}
                                        <span className="text-xs font-normal text-muted-foreground">
                                            #{group.project_id}
                                        </span>
                                    </CardTitle>
                                    <div className="flex gap-2">
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => navigate(`/project/${group.project_id}/metadata/new`)}
                                        >
                                            <Plus className="h-3.5 w-3.5 mr-1" />
                                            New Sheet
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => navigate(`/project/${group.project_id}/metadata`)}
                                        >
                                            <ExternalLink className="h-3.5 w-3.5 mr-1" />
                                            View All
                                        </Button>
                                    </div>
                                </div>
                            </CardHeader>

                            <CardContent className="pt-4">
                                {group.sheets.length === 0 ? (
                                    /* No sheets in this project */
                                    <div className="flex items-center justify-between py-4 px-3 rounded-lg bg-muted/30 text-sm text-muted-foreground">
                                        <span>No metadata sheets yet</span>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => navigate(`/project/${group.project_id}/metadata/new`)}
                                        >
                                            <Plus className="h-3.5 w-3.5 mr-1" />
                                            Create First Sheet
                                        </Button>
                                    </div>
                                ) : (
                                    <div className="rounded-lg border overflow-hidden">
                                        <table className="w-full text-sm">
                                            <thead className="bg-muted/50">
                                                <tr className="border-b">
                                                    <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Sheet</th>
                                                    <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Type</th>
                                                    <th className="text-right px-4 py-2.5 font-medium text-muted-foreground">Rows</th>
                                                    <th className="text-right px-4 py-2.5 font-medium text-muted-foreground">Cols</th>
                                                    <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Last Updated</th>
                                                    <th className="text-right px-4 py-2.5 font-medium text-muted-foreground">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {/* Sort: batches in order, then master */}
                                                {[...group.sheets]
                                                    .sort((a, b) => {
                                                        const order = { project: 0, batch: 1, master: 2 };
                                                        if ((order[a.type] ?? 1) !== (order[b.type] ?? 1))
                                                            return (order[a.type] ?? 1) - (order[b.type] ?? 1);
                                                        return (a.batch_index ?? 0) - (b.batch_index ?? 0);
                                                    })
                                                    .map((sheet) => {
                                                        const label = sheet.type === 'batch'
                                                            ? `Batch ${sheet.batch_index} Metadata`
                                                            : sheet.name;
                                                        return (
                                                            <tr
                                                                key={sheet.id}
                                                                className="border-b last:border-0 hover:bg-muted/30 transition-colors"
                                                            >
                                                                <td className="px-4 py-3 font-medium">{label}</td>
                                                                <td className="px-4 py-3">
                                                                    <TypeBadge type={sheet.type} />
                                                                </td>
                                                                <td className="px-4 py-3 text-right text-muted-foreground font-mono text-xs">
                                                                    {sheet.row_count}
                                                                </td>
                                                                <td className="px-4 py-3 text-right text-muted-foreground font-mono text-xs">
                                                                    {sheet.column_count}
                                                                </td>
                                                                <td className="px-4 py-3 text-muted-foreground text-xs">
                                                                    {sheet.updated_at
                                                                        ? new Date(sheet.updated_at).toLocaleDateString('en-US', {
                                                                            month: 'short', day: 'numeric', year: 'numeric'
                                                                          })
                                                                        : '—'}
                                                                </td>
                                                                <td className="px-4 py-3">
                                                                    <div className="flex justify-end gap-1.5">
                                                                        <Button
                                                                            size="sm"
                                                                            variant="outline"
                                                                            onClick={() => navigate(`/project/${group.project_id}/metadata`, {
                                                                                state: { activeSheetId: sheet.id }
                                                                            })}
                                                                        >
                                                                            <ChevronRight className="h-3.5 w-3.5 mr-1" />
                                                                            View
                                                                        </Button>
                                                                        <Button
                                                                            size="sm"
                                                                            variant="outline"
                                                                            onClick={() => navigate(`/project/${group.project_id}/metadata/${sheet.id}/edit`)}
                                                                        >
                                                                            <Pencil className="h-3.5 w-3.5 mr-1" />
                                                                            Edit
                                                                        </Button>
                                                                        <Button
                                                                            size="sm"
                                                                            variant="outline"
                                                                            onClick={() => handleDownload(sheet.id, sheet.name)}
                                                                        >
                                                                            <Download className="h-3.5 w-3.5" />
                                                                        </Button>
                                                                        <Button
                                                                            size="sm"
                                                                            variant="outline"
                                                                            onClick={() => setDeleteTarget({ sheetId: sheet.id, sheetName: label, projectId: group.project_id })}
                                                                            className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950"
                                                                        >
                                                                            <Trash2 className="h-3.5 w-3.5" />
                                                                        </Button>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </motion.div>
                ))}
            </main>
        </div>

        {/* New Sheet — project selector modal */}
        {newSheetOpen && (
            <div
                className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
                onClick={() => setNewSheetOpen(false)}
            >
                <div
                    className="bg-card border rounded-xl shadow-xl p-6 w-full max-w-sm mx-4 space-y-4"
                    onClick={(e) => e.stopPropagation()}
                >
                    <h2 className="text-lg font-semibold">New Metadata Sheet</h2>
                    <div className="space-y-1.5">
                        <label className="text-sm font-medium">Project</label>
                        <select
                            value={newSheetProjectId}
                            onChange={(e) => setNewSheetProjectId(e.target.value)}
                            className="w-full text-sm border rounded-md px-3 py-2 bg-background"
                        >
                            {groups.map(g => (
                                <option key={g.project_id} value={g.project_id}>
                                    {g.project_name || `Project #${g.project_id}`}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="flex justify-end gap-2 pt-1">
                        <Button variant="outline" onClick={() => setNewSheetOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            onClick={() => {
                                setNewSheetOpen(false);
                                navigate(`/project/${newSheetProjectId}/metadata/new`);
                            }}
                            disabled={!newSheetProjectId}
                        >
                            Create Sheet
                        </Button>
                    </div>
                </div>
            </div>
        )}

        <ConfirmModal
            open={deleteTarget !== null}
            title="Delete this sheet?"
            description={deleteTarget ? `"${deleteTarget.sheetName}" will be permanently deleted along with all its version history. This cannot be undone.` : ''}
            confirmLabel={deleting ? 'Deleting...' : 'Delete'}
            destructive
            onConfirm={handleDeleteSheet}
            onCancel={() => setDeleteTarget(null)}
        />
        </>
    );
}
